import debounce from 'lodash.debounce';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Edit } from 'react-feather';
import { Link } from 'react-router-dom';
import DateRangeSelector from '../components/date-range-selector';
import ExpirationPaymentModal from '../components/expiration-payment-modal';
import Paginator from '../components/paginator';
import RemittanceModal from '../components/remittance-modal';
import Table from '../components/table';
import { DateRange } from '../enums/date-range';
import { ExpirationPaymentStatus } from '../enums/expiration-payment-status';
import { Order } from '../enums/order';
import { DateRangeData } from '../interfaces/date-range-data';
import { ExpirationPayment } from '../interfaces/expiration-payment';
import { ExpirationPaymentsQuery } from '../interfaces/expiration-payments.query';
import { PaginatedDto } from '../interfaces/paginated.dto';
import { RemittanceDto } from '../interfaces/remittance.dto';
import { api } from '../services/api';
import { Constants } from '../services/constants';
import { expirationPaymentStatusToString, formatNumber, getDatesGivenDateRange } from '../services/helpers';
import { SelectColumnFilter } from '../services/table-helpers';
import myToastr from '../services/toastr';

const dateRange: DateRange = DateRange.ThisYear;

const ExpirationsView = () => {
  const tableRef: any = useRef<any>(null);
  const [selectedExpirationPayment, setSelectedExpirationPayment] = useState<ExpirationPayment | null>(null);
  const [showExpirationPaymentModal, setShowExpirationPaymentModal] = useState(false);
  const [dateRangeData, setDateRangeData] = useState<DateRangeData>({
    dateRange,
    dates: getDatesGivenDateRange(dateRange),
  });
  const [requesting, setRequesting] = useState<boolean>(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [paginatedData, setPaginatedData] = useState<PaginatedDto<ExpirationPayment> | null>(null);
  const [params, setParams] = useState<ExpirationPaymentsQuery>({
    endDate: moment(dateRangeData.dates[1]).format('YYYY-MM-DD HH:mm'),
    limit: Constants.LIMIT_RESULTS,
    order: Order.Desc,
    orderField: 'expirationDate',
    page: 1,
    startDate: moment(dateRangeData.dates[0]).format('YYYY-MM-DD HH:mm'),
    query: undefined,
    status: undefined,
  });
  const [query, setQuery] = useState('');
  const [selectedRemittanceId, setSelectedRemittanceId] = useState<number | null>(null);
  const [showRemittanceModal, setShowRemittanceModal] = useState(false);

  const debounced = useMemo(
    () =>
      debounce((nextValue: string) => {
        setParams({
          ...params,
          query: nextValue.length > 0 ? nextValue : undefined,
          page: 1,
        });
      }, 1000),
    [params],
  );

  const columns = useMemo(
    () => [
      {
        Header: 'Seleccionar',
        disableFilters: true,
        width: 20,
        Cell: ({ row }: any) => {
          const expirationPayment = row.original as ExpirationPayment;
          const hasBankAccountNumber = Boolean(
            expirationPayment.expiration?.invoice?.customer?.bankAccountNumber || expirationPayment.expiration?.invoice?.organizationClient?.relatedOrganization?.sepaIban,
          );
          if (!hasBankAccountNumber || expirationPayment.status !== ExpirationPaymentStatus.Pending || expirationPayment.remittanceId) {
            return null;
          }
          return (
            <input
              checked={selectedIds.includes(expirationPayment.id)}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedIds([...selectedIds, expirationPayment.id]);
                } else {
                  setSelectedIds(selectedIds.filter((id) => id !== expirationPayment.id));
                }
              }}
              style={{ width: 15, height: 15 }}
              type="checkbox"
            />
          );
        },
      },
      {
        Header: 'Factura',
        disableFilters: true,
        accessor: 'expiration.invoice.internalId',
        filter: 'fuzzyText',
        Cell: ({ value, row }: any) => {
          if (!value) {
            return null;
          }
          const expirationPayment = row.original as ExpirationPayment;
          if (!expirationPayment.expiration.invoice) {
            return null;
          }
          return (
            <Link to={`/factura/${expirationPayment.expiration.invoice.id}`} className="link-sale">
              <span className="fw-bold">#{value}</span>
            </Link>
          );
        },
      },
      {
        Header: 'Fecha factura',
        disableFilters: true,
        id: 'invoice.date',
        accessor: (e: ExpirationPayment) => (e.expiration.invoice ? moment(e.expiration.invoice.date).format('DD/MM/YYYY') : ''),
        filter: 'fuzzyDate',
      },
      {
        Header: 'Cliente',
        disableFilters: true,
        accessor: (e: ExpirationPayment) => {
          const customer = e.expiration.invoice?.customer;
          const relatedOrganization = e.expiration.invoice?.organizationClient?.relatedOrganization;
          if (!customer && !relatedOrganization) {
            return '';
          }
          if (customer) {
            return `${customer.name || ''} ${customer.surnames || ''}`.trim();
          }
          return relatedOrganization!.name;
        },
      },
      {
        Header: 'Remesa',
        disableFilters: true,
        accessor: 'remittance.internalId',
        filter: 'fuzzyText',
        Cell: ({ row }: any) => {
          const expirationPayment = row.original as ExpirationPayment;
          if (!expirationPayment.remittance) {
            return null;
          }
          return (
            <span
              className="link-sale fw-bold cursor-pointer"
              onClick={() => {
                setSelectedRemittanceId(expirationPayment.remittanceId);
                setShowRemittanceModal(true);
              }}
            >
              {expirationPayment.remittance.internalId}
            </span>
          );
        },
      },
      {
        Header: 'Total',
        disableFilters: true,
        accessor: 'expiration.total',
        Cell: ({ value }: any) => {
          return `${formatNumber(value)} €`;
        },
      },
      {
        Header: 'Referencia pago',
        disableFilters: true,
        accessor: 'internalNumber',
        Cell: ({ value }: any) => {
          return <span className="fw-bold">{value}</span>;
        },
      },
      {
        Header: 'Cantidad',
        disableFilters: true,
        accessor: 'amount',
        Cell: ({ value }: any) => {
          return `${formatNumber(value)} €`;
        },
      },
      {
        Header: 'Fecha cobro',
        disableFilters: true,
        accessor: (e: ExpirationPayment) => moment(e.expirationDate).format('DD/MM/YYYY'),
      },
      {
        Header: 'Estado',
        disableFilters: true,
        accessor: (e: ExpirationPayment) => expirationPaymentStatusToString(e.status),
        Filter: SelectColumnFilter,
      },
      {
        Header: 'Acciones',
        disableFilters: true,
        Cell: ({ row }: any) => {
          const expirationPayment = row.original as ExpirationPayment;
          return (
            <div className="d-flex flex-row align-items-center gap-2">
              <div title="Actualizar estado">
                <Edit
                  color="#222E3D"
                  onClick={() => {
                    setSelectedExpirationPayment(expirationPayment);
                    setShowExpirationPaymentModal(true);
                  }}
                  size={16}
                  type="button"
                />
              </div>
            </div>
          );
        },
      },
    ],
    [selectedIds],
  );

  const getPaginatedData = useCallback(async () => {
    setRequesting(true);
    try {
      const paginatedData = await api.getExpirationPayments(params);
      setPaginatedData(paginatedData);
    } catch (e: any) {
      myToastr.error(e.response.data.message);
    }
    setRequesting(false);
  }, [params]);

  const onExpirationPaymentModalClose = (result: boolean) => {
    if (result) {
      getPaginatedData();
    }
    setShowExpirationPaymentModal(false);
    setSelectedExpirationPayment(null);
  };

  const createRemittance = async () => {
    setRequesting(true);
    try {
      const remittanceDto: RemittanceDto = {
        expirationPaymentIds: selectedIds,
      };
      await api.createRemittance(remittanceDto);
      myToastr.success('Remesa creada correctamente');
      setSelectedIds([]);
      getPaginatedData();
    } catch (e: any) {
      myToastr.error(e.response.data.message);
    }
    setRequesting(false);
  };

  useEffect(() => {
    getPaginatedData();
  }, [params]);

  useEffect(() => {
    return () => {
      debounced.cancel();
    };
  }, [debounced]);

  return (
    <div className="expirations p-4">
      <div className="d-flex flex-row align-items-center mb-2">
        <h1 className="flex-grow-1">Vencimientos</h1>
        <input
          className="me-2"
          onChange={(e) => {
            const nextValue = e.target.value;
            setQuery(nextValue);
            debounced(nextValue);
          }}
          placeholder="Buscar..."
          style={{ padding: '4px 10px', fontSize: 14, width: 250, border: '1px solid black', borderRadius: 8 }}
          value={query}
          type="text"
        />
        <select
          style={{
            padding: '5px 10px',
            marginRight: 10,
            fontSize: 14,
          }}
          onChange={(e) => {
            setParams({
              ...params,
              status: e.target.value ? (e.target.value as ExpirationPaymentStatus) : undefined,
              page: 1,
            });
          }}
          value={params.status || ''}
        >
          <option value="">Todos los estados</option>
          <option value={ExpirationPaymentStatus.Pending}>{expirationPaymentStatusToString(ExpirationPaymentStatus.Pending)}</option>
          <option value={ExpirationPaymentStatus.Paid}>{expirationPaymentStatusToString(ExpirationPaymentStatus.Paid)}</option>
          <option value={ExpirationPaymentStatus.Remitted}>{expirationPaymentStatusToString(ExpirationPaymentStatus.Remitted)}</option>
          <option value={ExpirationPaymentStatus.Returned}>{expirationPaymentStatusToString(ExpirationPaymentStatus.Returned)}</option>
        </select>
        <button
          className="me-2 clear-filters"
          disabled={requesting}
          onClick={() => {
            if (selectedIds.length === 0) {
              myToastr.info('Debes seleccionar al menos un vencimiento para crear una remesa.');
              return;
            }
            createRemittance();
          }}
        >
          Crear remesa
        </button>
        <div className="col-md-2">
          <DateRangeSelector
            showTimePicker={false}
            dateRangeData={dateRangeData}
            availableDateRanges={[DateRange.Today, DateRange.Yesterday, DateRange.ThisWeek, DateRange.ThisMonth, DateRange.ThisYear, DateRange.LastYear]}
            onChange={(drd: DateRangeData) => {
              setDateRangeData(drd);
              setParams({
                ...params,
                startDate: moment(drd.dates[0]).format('YYYY-MM-DD HH:mm'),
                endDate: moment(drd.dates[1]).format('YYYY-MM-DD HH:mm'),
                page: 1,
              });
            }}
          />
        </div>
        <button
          className="ms-2 clear-filters"
          disabled={requesting}
          onClick={() => {
            tableRef.current?.clearFilters();
            setDateRangeData({
              dateRange,
              dates: getDatesGivenDateRange(dateRange),
            });
          }}
        >
          Limpiar filtros
        </button>
      </div>
      <Table columns={columns} data={paginatedData ? paginatedData.results : []} noDataMessage="No hay vencimientos" ref={tableRef} />
      {paginatedData && (
        <Paginator
          paginatedDto={paginatedData}
          goToPage={(page) =>
            setParams({
              ...params,
              page,
            })
          }
        />
      )}
      <ExpirationPaymentModal closeModal={onExpirationPaymentModalClose} open={showExpirationPaymentModal} expirationPayment={selectedExpirationPayment} />
      <RemittanceModal
        closeModal={async () => {
          setSelectedRemittanceId(null);
          setShowRemittanceModal(false);
        }}
        onExpirationPaymentUpdate={() => getPaginatedData()}
        remittanceId={selectedRemittanceId}
        show={showRemittanceModal}
      />
    </div>
  );
};

export default ExpirationsView;
